import React, { useState } from 'react';
import { useContent } from '../../context/ContentContext';
import { SpecialForYouItem, MenuItem, OrderType } from '../../types';
import { resolveMediaUrl } from '../../utils/api';
import { formatRupiah } from '../../utils/formatters';
import {
  ArrowLeft,
  Calendar,
  Tag,
  CheckCircle2,
  Clock,
  Sparkles,
  ShoppingBag,
  Share2,
  AlertCircle,
  Copy,
  Store,
  Motorbike,
  ExternalLink,
} from 'lucide-react';
import { isSpecialPromoActive } from './PromoCatalogPage';

interface PromoDetailPageProps {
  promoId: string;
  onBack: () => void;
  onOpenOrder?: (item?: MenuItem, initialType?: OrderType, promoItem?: SpecialForYouItem) => void;
}

const DEFAULT_BANNER =
  'https://images.unsplash.com/photo-1514432324607-a09d9b4aefdd?auto=format&fit=crop&w=1200&q=80';

export const PromoDetailPage: React.FC<PromoDetailPageProps> = ({
  promoId,
  onBack,
  onOpenOrder,
}) => {
  const { data, showToast } = useContent();
  const [copied, setCopied] = useState<boolean>(false);

  // Search promo by exact id or match normalized
  const promo: SpecialForYouItem | undefined = React.useMemo(() => {
    const list = Array.isArray(data.specialForYou) ? data.specialForYou : [];
    if (!promoId) return undefined;
    return (
      list.find((item) => item.id === promoId) ||
      list.find((item) => item.id.toLowerCase() === promoId.toLowerCase()) ||
      list.find(
        (item) =>
          encodeURIComponent(item.title.toLowerCase().replace(/\s+/g, '-')) ===
          promoId.toLowerCase()
      )
    );
  }, [data.specialForYou, promoId]);

  // Process terms & conditions into array of bullet points
  const termsList: string[] = React.useMemo(() => {
    if (!promo?.terms_and_conditions || !promo.terms_and_conditions.trim()) {
      return [
        'Berlaku untuk pemesanan Dine In, Take Away, dan Delivery.',
        'Berlaku selama periode promo masih berlangsung dan kuota harian tersedia.',
        'Promo berlaku di Chapter 5 Sudirman & Chapter 6 Kelakap 7.',
        'Poin loyalty akan otomatis bertambah ke akun Anda setelah pembayaran sukses.',
        'LetonKu berhak mengubah ketentuan promo sewaktu-waktu tanpa pemberitahuan sebelumnya.',
      ];
    }
    return promo.terms_and_conditions
      .split('\n')
      .map((t) => t.trim().replace(/^[•\-\*]\s*/, ''))
      .filter(Boolean);
  }, [promo?.terms_and_conditions]);

  const handleShare = async () => {
    try {
      const shareUrl = `${window.location.origin}${window.location.pathname}#promo?id=${encodeURIComponent(
        promo?.id || promoId
      )}`;
      if (navigator.share) {
        await navigator.share({
          title: promo ? `${promo.title} — Promo LetonKu` : 'Promo LetonKu',
          text: promo?.description || 'Cek promo spesial dari LetonKu!',
          url: shareUrl,
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        showToast('Link promo berhasil disalin ke clipboard!', 'success');
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      // User cancelled or clipboard fallback
    }
  };

  const isPromoActive = React.useMemo(() => isSpecialPromoActive(promo), [promo]);
  const bannerImage = (promo ? resolveMediaUrl(promo.image_url) : null) || DEFAULT_BANNER;

  const handleOrderNow = () => {
    if (!isPromoActive) return;
    if (onOpenOrder && promo) {
      const orderType: OrderType = promo.id.toLowerCase().includes('delivery') ? 'DELIVERY' : 'TAKE AWAY';
      onOpenOrder(undefined, orderType, promo);
    } else {
      window.location.hash = '#order';
    }
  };

  // If promo not found
  if (!promo) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col justify-between text-[#0A192F]">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-[#E2E8F0] px-4 py-3.5">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-[#0056D2] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Kembali</span>
            </button>
            <span className="font-bold text-sm text-[#0A192F]">Detail Promo</span>
            <div className="w-16" />
          </div>
        </header>

        {/* Empty Body */}
        <div className="max-w-md mx-auto px-6 py-20 text-center flex-1 flex flex-col items-center justify-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8" />
          </div>
          <h2 className="font-display font-black text-xl text-[#0A192F] mb-2">
            Promo Tidak Ditemukan
          </h2>
          <p className="text-sm text-slate-600 mb-8 leading-relaxed">
            Promo yang Anda cari mungkin sudah berakhir, dipindahkan, atau tautan tidak valid. Silakan cek promo aktif lainnya di LetonKu.
          </p>
          <button
            type="button"
            onClick={onBack}
            className="w-full py-3.5 px-6 rounded-xl bg-[#0056D2] hover:bg-[#0046AB] text-white font-bold text-sm shadow-md shadow-[#0056D2]/20 transition-all cursor-pointer"
          >
            Kembali ke Beranda
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col text-[#0A192F]">
      {/* Top Sticky Navigation Bar */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-[#E2E8F0] px-4 py-3.5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-slate-700 hover:text-[#0056D2] hover:bg-slate-100 transition-all cursor-pointer font-semibold text-xs sm:text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Kembali</span>
          </button>

          <div className="flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-[#0056D2]" />
            <span className="font-bold text-sm tracking-tight text-[#0A192F]">
              PROMO UNTUKMU
            </span>
          </div>

          <button
            type="button"
            onClick={handleShare}
            aria-label="Bagikan Promo"
            title="Bagikan Promo"
            className="p-2 rounded-xl text-slate-600 hover:text-[#0056D2] hover:bg-slate-100 transition-colors cursor-pointer"
          >
            {copied ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            ) : (
              <Share2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 py-6 pb-28">
        {/* Banner Section */}
        <div className="relative w-full aspect-video sm:aspect-[21/9] rounded-3xl overflow-hidden bg-slate-200 shadow-sm border border-[#E2E8F0] mb-6">
          <img
            src={bannerImage}
            alt={promo.title}
            className="w-full h-full object-cover"
            loading="eager"
            referrerPolicy="no-referrer"
            onError={(e) => {
              const target = e.currentTarget;
              if (target.src !== DEFAULT_BANNER) {
                target.src = DEFAULT_BANNER;
              }
            }}
          />
          {/* Status Badge Overlay */}
          <div className="absolute top-4 left-4 flex flex-wrap gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold shadow-md backdrop-blur-md ${
                isPromoActive
                  ? 'bg-emerald-600/90 text-white'
                  : 'bg-rose-600/90 text-white'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  isPromoActive ? 'bg-emerald-300 animate-pulse' : 'bg-rose-300'
                }`}
              />
              {isPromoActive ? 'PROMO AKTIF' : 'PROMO BERAKHIR'}
            </span>

            {promo.badge_text && (
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-[#0056D2]/90 text-white shadow-md backdrop-blur-md">
                {promo.badge_text}
              </span>
            )}
          </div>
        </div>

        {/* Promo Header Info */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#E2E8F0] shadow-sm mb-6 space-y-5">
          <div className="space-y-2">
            <span className="inline-block text-[11px] font-bold uppercase tracking-wider text-[#0056D2] bg-blue-50 px-3 py-1 rounded-lg border border-blue-100">
              LETONKU SPECIAL PROMO
            </span>
            <h1 className="font-display font-black text-2xl sm:text-3xl text-[#0A192F] tracking-tight leading-snug">
              {promo.title}
            </h1>
          </div>

          {/* Period & Pricing Row */}
          <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-slate-100 text-sm">
            {(promo.start_date || promo.end_date) && (
              <div className="flex items-center gap-2 text-slate-600 bg-slate-50 px-3.5 py-2 rounded-xl border border-slate-200">
                <Calendar className="w-4 h-4 text-[#0056D2]" />
                <span className="text-xs sm:text-sm font-medium">
                  Periode: <strong className="text-slate-800">{promo.start_date || 'Mulai Sekarang'}</strong> —{' '}
                  <strong className="text-slate-800">{promo.end_date || 'Selesai'}</strong>
                </span>
              </div>
            )}

            {promo.price > 0 && (
              <div className="flex items-center gap-2 text-[#0056D2] bg-blue-50 px-3.5 py-2 rounded-xl border border-blue-100">
                <Tag className="w-4 h-4" />
                <span className="text-xs sm:text-sm font-bold">
                  Nilai Promo: {formatRupiah(promo.price)}
                </span>
              </div>
            )}
          </div>

          {/* Benefit / Discount Highlight Box */}
          {promo.discount_info && (
            <div className="bg-gradient-to-r from-blue-50 via-indigo-50/50 to-blue-50 border border-blue-200 rounded-2xl p-4 sm:p-5 flex items-start gap-3.5">
              <div className="w-10 h-10 rounded-xl bg-[#0056D2] text-white flex items-center justify-center shrink-0 shadow-sm">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-sm sm:text-base text-[#0A192F]">
                  Keuntungan Promo
                </h4>
                <p className="text-xs sm:text-sm text-slate-600 mt-0.5 leading-relaxed">
                  {promo.discount_info}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Description Section */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#E2E8F0] shadow-sm mb-6 space-y-3">
          <h3 className="font-bold text-lg text-[#0A192F] flex items-center gap-2">
            <span>Deskripsi Promo</span>
          </h3>
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
            {promo.description}
          </p>
        </div>

        {/* Syarat & Ketentuan Section */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#E2E8F0] shadow-sm mb-6 space-y-4">
          <h3 className="font-bold text-lg text-[#0A192F] flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-[#0056D2]" />
            <span>Syarat & Ketentuan</span>
          </h3>
          <ul className="space-y-2.5">
            {termsList.map((term, index) => (
              <li
                key={`term-${index}`}
                className="flex items-start gap-3 text-xs sm:text-sm text-slate-600 leading-relaxed"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#0056D2] mt-2 shrink-0" />
                <span>{term}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Outlet & Delivery Support Information */}
        <div className="bg-white rounded-3xl p-6 border border-[#E2E8F0] shadow-sm mb-6">
          <h4 className="font-bold text-sm text-[#0A192F] mb-3">
            Layanan Pemesanan Tersedia
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-200 text-xs text-slate-700">
              <div className="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-[#0056D2] shrink-0">
                <Store className="w-4 h-4" />
              </div>
              <div>
                <strong className="block font-bold text-slate-800">Dine In & Take Away</strong>
                <span>Chapter 5 Sudirman & Chapter 6 Kelakap</span>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-200 text-xs text-slate-700">
              <div className="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-[#0056D2] shrink-0">
                <Motorbike className="w-4 h-4" />
              </div>
              <div>
                <strong className="block font-bold text-slate-800">Pesan Antar (Delivery)</strong>
                <span>Pengantaran langsung oleh Kurir Resmi LetonKu</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Sticky Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E2E8F0] px-4 py-3 sm:py-4 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 sm:flex-none sm:px-6 py-3.5 rounded-2xl border border-slate-300 hover:border-slate-400 bg-white text-slate-700 font-bold text-xs sm:text-sm transition-all cursor-pointer text-center"
          >
            KEMBALI
          </button>

          <button
            type="button"
            onClick={handleOrderNow}
            disabled={!isPromoActive}
            className={`flex-[2] sm:flex-1 py-3.5 px-6 rounded-2xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 shadow-md transition-all cursor-pointer ${
              isPromoActive
                ? 'bg-[#0056D2] hover:bg-[#0046AB] text-white shadow-[#0056D2]/25 active:scale-[0.99]'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
          >
            <ShoppingBag className="w-4 h-4" />
            <span>{isPromoActive ? promo.cta_text || 'PESAN SEKARANG' : 'PROMO BERAKHIR'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
